import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { Box, Stack, Drawer, Typography, Link } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// config
import { NAV } from '../../../config-global';
// components
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';
//
import navConfig from './config-navigation';
import NavToggleButton from './NavToggleButton';
import Iconify from '../../../components/iconify';
import { useAuthContext } from '../../../auth/useAuthContext';

// ----------------------------------------------------------------------

type Props = {
  openNav: boolean;
  onCloseNav: VoidFunction;
};

export default function NavVertical({ openNav, onCloseNav }: Props) {
  const { pathname } = useLocation();
  const { user } = useAuthContext();
  const showSupport = user?.role === 'pubManager' || user?.role === 'advManager';

  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
        }}
      >
        <Logo />
      </Stack>

      <NavSectionVertical data={navConfig} />

      <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
        {showSupport && (
          <Box sx={{ display: 'flex', marginTop: 'auto', flexDirection: 'column', mb: 2 }}>
            <Typography textAlign="center" sx={{ mb: '2px' }}>
              <strong>Support</strong>
            </Typography>
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                paddingLeft: 3,
                mb: '1px',
                columnGap: '4px',
              }}
            >
              <Iconify icon="eva:email-outline" color="primary.main" width={16} />
              <Link color="info.main" href="mailto:alon@adventurefeeds.com">
                alon@adventurefeeds.com
              </Link>
            </Typography>
            <Typography
              sx={{ display: 'flex', alignItems: 'center', paddingLeft: 3, columnGap: '4px' }}
            >
              <Iconify icon="logos:microsoft-teams" color="primary.main" width={16} />
              <Link
                color="info.main"
                href="https://teams.microsoft.com/l/chat/0/0?users=Alonhollander1@outlook.com"
              >
                Alonhollander1@outlook.com
              </Link>
            </Typography>
          </Box>
        )}
      </Box>
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_DASHBOARD },
      }}
    >
      <NavToggleButton />

      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              zIndex: 0,
              width: NAV.W_DASHBOARD,
              bgcolor: 'transparent',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV.W_DASHBOARD,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
